// Deterministische, regelbasierte Stromkostenberechnung (PRD §15).
// Keine KI, keine externen Aufrufe. Alle Werte clientseitig.
//
// Drei Berechnungsmodi:
//   - "watt"          : Leistung in Watt + Stunden/Tag + Tage/Woche
//   - "kwh_per_cycle" : kWh pro Durchgang + Durchgänge/Woche
//   - "kwh_per_year"  : kWh pro Jahr
//
// Konvention: Monat = Woche × 4,345 (PRD §15), Jahr = Woche × 52.
// Für kwh_per_year: Monat = Jahr / 12, Tag = Jahr / 365 (PRD §15).

import priceData from "@/data/electricity-price.json";

export const WEEKS_PER_MONTH = 4.345;
export const WEEKS_PER_YEAR = 52;
// Offizieller deutscher Endkundenpreis (Eurostat, inkl. Steuern), beim Build
// gebacken via scripts/fetch-electricity-price.js. Editierbar im Rechner.
export const DEFAULT_PRICE = priceData.pricePerKwh; // €/kWh
export const PRICE_META = priceData; // { pricePerKwh, period, source, fetchedAt }

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Watt-basiert.
 * @param {number} watt        Leistung in Watt
 * @param {number} hoursPerDay Stunden pro Nutzungstag
 * @param {number} daysPerWeek Nutzungstage pro Woche
 * @param {number} price       Strompreis €/kWh
 */
export function calcWatt({ watt, hoursPerDay, daysPerWeek, price }) {
  const kwhPerDay = (watt * hoursPerDay) / 1000; // kWh = W × h ÷ 1000
  const costPerUse = kwhPerDay * price; // = Kosten pro Nutzungstag
  const weekKwh = kwhPerDay * daysPerWeek;
  const weekCost = costPerUse * daysPerWeek;
  return {
    kwhPerUse: kwhPerDay,
    kwhPerWeek: weekKwh,
    kwhPerYear: weekKwh * WEEKS_PER_YEAR,
    perUse: costPerUse,
    perDay: costPerUse, // ein Nutzungstag
    perWeek: weekCost,
    perMonth: weekCost * WEEKS_PER_MONTH,
    perYear: weekCost * WEEKS_PER_YEAR,
  };
}

/**
 * kWh pro Durchgang (Trockner, Waschmaschine, Spülmaschine).
 * @param {number} kwhPerCycle    kWh pro Durchgang
 * @param {number} cyclesPerWeek  Durchgänge pro Woche
 * @param {number} price          Strompreis €/kWh
 */
export function calcPerCycle({ kwhPerCycle, cyclesPerWeek, price }) {
  const costPerCycle = kwhPerCycle * price;
  const weekKwh = kwhPerCycle * cyclesPerWeek;
  const weekCost = costPerCycle * cyclesPerWeek;
  return {
    kwhPerUse: kwhPerCycle,
    kwhPerWeek: weekKwh,
    kwhPerYear: weekKwh * WEEKS_PER_YEAR,
    perUse: costPerCycle,
    perDay: weekCost / 7,
    perWeek: weekCost,
    perMonth: weekCost * WEEKS_PER_MONTH,
    perYear: weekCost * WEEKS_PER_YEAR,
  };
}

/**
 * kWh pro Jahr (Kühlschrank, Gefrierschrank).
 * @param {number} kwhPerYear kWh pro Jahr
 * @param {number} price      Strompreis €/kWh
 */
export function calcPerYear({ kwhPerYear, price }) {
  const yearCost = kwhPerYear * price;
  return {
    kwhPerUse: kwhPerYear / 365, // pro Tag
    kwhPerWeek: kwhPerYear / WEEKS_PER_YEAR,
    kwhPerYear,
    perUse: yearCost / 365,
    perDay: yearCost / 365,
    perWeek: yearCost / WEEKS_PER_YEAR,
    perMonth: yearCost / 12,
    perYear: yearCost,
  };
}

/**
 * Zentraler Dispatcher: berechnet ein Ergebnis für gegebene Eingaben nach Modus.
 * @param {"watt"|"kwh_per_cycle"|"kwh_per_year"} mode
 * @param {object} inputs
 */
export function calc(mode, inputs) {
  switch (mode) {
    case "watt":
      return calcWatt(inputs);
    case "kwh_per_cycle":
      return calcPerCycle(inputs);
    case "kwh_per_year":
      return calcPerYear(inputs);
    default:
      throw new Error(`Unbekannter Berechnungsmodus: ${mode}`);
  }
}

/**
 * Berechnet niedrig / realistisch / hoch. Erwartet drei Eingabe-Objekte
 * (oder ein gemeinsames Nutzungsprofil + drei Verbrauchswerte).
 * Liefert { low, typical, high } mit je einem calc-Ergebnis.
 */
export function calcRange(mode, { low, typical, high }) {
  return {
    low: calc(mode, low),
    typical: calc(mode, typical),
    high: calc(mode, high),
  };
}

// Formatierung -----------------------------------------------------------

const eur = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const eur0 = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatEuro(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return eur.format(n);
}

export function formatEuroCompact(n) {
  if (n == null || Number.isNaN(n)) return "—";
  // ab 100 € ohne Nachkommastellen für ruhigere Spannen
  return Math.abs(n) >= 100 ? eur0.format(n) : eur.format(n);
}

export function formatKwh(n) {
  if (n == null || Number.isNaN(n)) return "—";
  const v = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: n < 10 ? 2 : 0,
    maximumFractionDigits: n < 10 ? 2 : 1,
  }).format(n);
  return `${v} kWh`;
}

export function formatNumber(n, digits = 2) {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(n);
}

export { round2 };
